'use client'

import { fetchDeepLink } from '@services/fetchDeepLink'
import React, { useEffect, useState } from 'react'
import { Overflow } from '@enums/OverflowTypes'
import { onDownloadApp } from '@components/MainWidget/common/helper'
import { widgetJourney } from '@configs/segmentEvents.config'
import { ExperimentProductType } from '@enums/experimentProductType'
import DesktopChallanModal from './Desktop/DesktopChallanModal'
import MobileChallanModal from './Mobile/MobileChallanModal'
import { getWidgetId } from '../../ChallanWidgetModals/constants'
import { DEFAULT_FINAL_MODAL_VARIATION, FALLBACK_MOBILE_MODAL_DATA, FALLBACK_DESKTOP_MODAL_DATA } from '../constants'

interface ChallanAppRedirectWrapperProps {
  challanFinalScreenData: any
  isMobile: boolean
  regNum: string
  mobileNo: string
  vehicleInfoData: Record<string, any>
  segmentEventCommonProperties: Record<string, string> | null
  setShowMobileChallanModal: Function
  isBottomFloater: boolean
  onHide: Function
  showMwebChallanModal: boolean
  setShowMwebChallanModal: Function
  widgetType: string
  ppedData: any
  expiryBracket: any
  challanData: any
  checkPPEDExperiment: boolean
}

const ChallanAppRedirectWrapper: React.FC<ChallanAppRedirectWrapperProps> = ({
  challanFinalScreenData,
  isMobile,
  regNum,
  mobileNo,
  vehicleInfoData,
  segmentEventCommonProperties,
  setShowMobileChallanModal,
  isBottomFloater,
  onHide,
  showMwebChallanModal,
  setShowMwebChallanModal,
  widgetType,
  ppedData,
  expiryBracket,
  challanData,
  checkPPEDExperiment,
}) => {
  const [downloadUrl, setDownloadUrl] = useState('')
  const [experimentSource, setExperimentSource] = useState<ExperimentProductType | ''>('')
  useEffect(() => {
    if (showMwebChallanModal || isMobile) {
      document.documentElement.style.overflowY = Overflow.Hidden
    }

    // push fake history to history
    if (showMwebChallanModal) {
      window.history.pushState({ modal: true }, '')
    }
  }, [showMwebChallanModal, isMobile])

  // Handle browser back button
  useEffect(() => {
    const onPop = () => {
      if (showMwebChallanModal) {
        handleMobileChallanModalClose()
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [showMwebChallanModal])

  useEffect(() => {
    let source: ExperimentProductType | '' = ''
    const isPrivateCar = vehicleInfoData?.vehicleInfo?.vehicle_type_v2 === 'PRIVATE_CAR'
    if (isPrivateCar) {
      if (checkPPEDExperiment) {
        source = expiryBracket?.isInBracket ? ExperimentProductType.CHALLAN_PPED_TST : ExperimentProductType.CHALLAN_NONPPED_TST
      } else {
        source = expiryBracket?.isInBracket ? ExperimentProductType.CHALLAN_PPED_CTRL : ExperimentProductType.CHALLAN_NONPPED_CTRL
      }
    }
    setExperimentSource(source)
    if (!isMobile) {
      const fetchData = async (regNo, widgetType, mobileNo, selectedProvider) => {
        const deepLink = await fetchDeepLink({
          regNo,
          widgetType,
          mobileNo,
          selectedProvider,
          ...(source && { experimentSource: source }),
        })
        setDownloadUrl(deepLink)
      }

      void fetchData(regNum, widgetType, mobileNo, vehicleInfoData?.selectedProvider)
    } else setShowMwebChallanModal(true)
  }, [checkPPEDExperiment, expiryBracket])

  useEffect(() => {
    const setWidgetDisplay = (display: string) => {
      const widgetElements = document.querySelectorAll(getWidgetId(widgetType))
      widgetElements.forEach(widgetElement => {
        const element = widgetElement as HTMLElement
        element.style.display = display
      })
    }

    if (isBottomFloater) {
      setWidgetDisplay('none')
    } else {
      setWidgetDisplay('')
    }

    return () => {
      setWidgetDisplay('')
    }
  }, [isBottomFloater])

  const handleMobileChallanModalClose = () => {
    if (onHide) {
      onHide()
    }

    document.documentElement.style.overflowY = Overflow.Scroll
    setShowMwebChallanModal(false)
  }

  return (
    <>
      {showMwebChallanModal && isMobile && (
        <MobileChallanModal
          regNum={regNum}
          {...((challanFinalScreenData?.mobileScreenData?.title
            ? challanFinalScreenData?.mobileScreenData
            : challanFinalScreenData?.mobileScreenData?.[DEFAULT_FINAL_MODAL_VARIATION]) || FALLBACK_MOBILE_MODAL_DATA)}
          onSubmit={() => {
            void onDownloadApp({
              regNo: vehicleInfoData?.reg_number,
              widgetType,
              phone: vehicleInfoData?.mobile_number,
              vehicleData: vehicleInfoData?.vehicleInfo,
              providerData: vehicleInfoData?.selectedProvider,
              ...(experimentSource && { experimentSource }),
              segmentEventCommonProperties: {
                page_type: `${widgetJourney?.[widgetType]}_check_variation_${DEFAULT_FINAL_MODAL_VARIATION}`,
                journey: widgetJourney?.[widgetType],
              },
            })
          }}
          segmentEventCommonProperties={segmentEventCommonProperties}
          widgetType={widgetType}
          vehicleInfoData={vehicleInfoData}
          PPEDData={ppedData}
          expiryBracket={expiryBracket}
          challanData={challanData}
          checkPPEDExperiment={checkPPEDExperiment}
        />
      )}
      {!isMobile && (
        <DesktopChallanModal
          show
          downloadUrl={downloadUrl}
          regNum={regNum}
          {...((challanFinalScreenData?.desktopScreenData?.title
            ? challanFinalScreenData?.desktopScreenData
            : challanFinalScreenData?.desktopScreenData?.[DEFAULT_FINAL_MODAL_VARIATION]) ||
            FALLBACK_DESKTOP_MODAL_DATA)}
          onClose={() => {
            setShowMobileChallanModal(false)
            document.documentElement.style.overflowY = Overflow.Scroll
          }}
          segmentEventCommonProperties={segmentEventCommonProperties}
          widgetType={widgetType}
          vehicleInfoData={vehicleInfoData}
          PPEDData={ppedData}
          expiryBracket={expiryBracket}
          challanData={challanData}
          checkPPEDExperiment={checkPPEDExperiment}
        />
      )}
    </>
  )
}

export default ChallanAppRedirectWrapper
